import {
  Box,
  Flex,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  useColorModeValue,
  Text as ChakraText,
} from "@chakra-ui/react";
import Card from "../../components/Card/Card";
import React, { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import API_CONFIG from "../../Configuration/apiConfig";
import apiClient from "../../Configuration/apiClient";
import { FaArrowLeft, FaEye } from "react-icons/fa";
import AgGridWrapper from "../../components/AgGrid/AgGridWrapper";

export default function VisibilityItemsView() {
  const textColor = useColorModeValue("gray.700", "white");
  const history = useHistory();
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [imageUrls, setImageUrls] = useState({
    actualImage: undefined,
    annotatedImage: undefined,
  });

  // Fetch table data
  useEffect(() => {
    const fetchTableData = async () => {
      setLoading(true);
      setError(null);
      try {
        const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.VISIBILITY_ITEMS}`;
        const response = await apiClient.get(url);
        const apiData = Array.isArray(response.data)
          ? response.data
          : [response.data];
        const transformedData = apiData.map((item, index) => ({
          rowIndex: index,
          storeName: item.store?.storeName?.toString() || "N/A",
          visibilityItem: item.product?.productName?.toString() || "N/A",
          details: item.value?.toString() || "0.0",
          fileName: item.fileName || "N/A",
          s3pathActualFile: item.s3pathActualFile || null,
          s3pathAnnotatedFile: item.s3pathAnnotatedFile || null,
        }));
        setTableData(transformedData);
      } catch (err) {
        setError("Failed to fetch data.");
      } finally {
        setLoading(false);
      }
    };
    fetchTableData();
  }, []);

  const handleBackToDashboard = () => {
    history.push("/admin/dashboard");
  };

  const handleEyeClick = async (rowData) => {
    setSelectedRow(rowData);
    setIsModalOpen(true);
    setImageUrls({ actualImage: undefined, annotatedImage: undefined });
  
    try {
      const bucketName = API_CONFIG.S3_BUCKETS.IMAGES;

      // helper: get presigned URL directly like StoreReportDashboard
      const fetchPresignedUrl = async (s3Path) => {
        if (!s3Path) return null;
        const { data: presignedUrl } = await apiClient.get(
          `${API_CONFIG.SERVICE_URL}${API_CONFIG.ENDPOINTS.S3.PRESIGNED_URL}?fileName=${encodeURIComponent(
            s3Path
          )}&bucketName=${bucketName}&operation=download`
        );
        return presignedUrl; // use it directly
      };
  
      const [actualImageUrl, annotatedImageUrl] = await Promise.all([
        fetchPresignedUrl(rowData.s3pathActualFile),
        fetchPresignedUrl(rowData.s3pathAnnotatedFile),
      ]);
  
      setImageUrls({
        actualImage: actualImageUrl,
        annotatedImage: annotatedImageUrl,
      });
    } catch (err) {
      console.error("Error fetching images:", err);
      setImageUrls({
        actualImage: null,
        annotatedImage: null,
      });
    }
  };
  

  const columnDefs = useMemo(
    () => [
      {
        headerName: "FileName",
        field: "fileName",
        // cellClass: "ag-numeric-cell",
        sortable: true,
        filter: true,
        width: 200,
        cellRenderer: (params) => (
          <Flex alignItems="center" gap="2">
            <ChakraText>{params.value || "N/A"}</ChakraText>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleEyeClick(params.data)}
            >
              <FaEye />
            </Button>
          </Flex>
        ),
      },
      {
        headerName: "StoreName",
        field: "storeName",
        sortable: true,
        filter: true,
        width: 100,
        valueFormatter: (params) => params.value || "N/A",
      },
      {
        headerName: "VisibilityItem",
        field: "visibilityItem",
        sortable: true,
        filter: true,
      },
      {
        headerName: "Details",
        field: "details",
        sortable: true,
        filter: true,
      },
    ],
    []
  );

  return (
    <Flex flexDirection="column" pt={{ base: "120px", md: "75px" }}>
      <Card mb="20px" p="20px">
        <Flex justify="space-between" align="center" mb="20px">
          <Flex alignItems="center" gap="4">
            <Button
              leftIcon={<FaArrowLeft />}
              onClick={handleBackToDashboard}
              size="sm"
              colorScheme="blue"
              variant="outline"
              borderRadius="5px"
            >
              Back
            </Button>
          </Flex>
        </Flex>
        <AgGridWrapper
          rowData={tableData}
          columnDefs={columnDefs}
          loading={loading}
          height="650px"
          noRowsMessage={error || "No data available"}
          extraGridProps={{ pagination: true, paginationPageSize: 8, headerHeight: 70 }}
        />
      </Card>
      <Modal
  isOpen={isModalOpen}
  onClose={() => setIsModalOpen(false)}
  size="4xl" // centered large modal
>
  <ModalOverlay />
  <ModalContent borderRadius="md">
    <ModalHeader>
      Image Details for {selectedRow?.fileName || "N/A"}
    </ModalHeader>
    <ModalCloseButton />
    <ModalBody>
      {selectedRow && (
        <Box mb="20px">
          <Flex justify="space-between" align="center">
            <ChakraText fontSize="lg" fontWeight="bold">
              Store Name: {selectedRow.storeName || "N/A"}
            </ChakraText>
            <ChakraText fontSize="lg" fontWeight="bold">
              Visibility Item: {selectedRow.visibilityItem || "N/A"}
            </ChakraText>
          </Flex>
        </Box>
      )}
      <Flex
        direction="row"
        gap="30px"
        justify="center"
        alignItems="center"
        flexWrap="wrap"
      >
        {/* Actual Image */}
        <Flex direction="column" alignItems="center" gap="10px">
          <ChakraText fontSize="lg" fontWeight="bold">
            Actual Image
          </ChakraText>
          <img
            src={
              imageUrls.actualImage ||
              "https://via.placeholder.com/400x400?text=No+Actual+Image"
            }
            alt="Actual"
            style={{
              maxWidth: "400px",
              maxHeight: "600px",
              objectFit: "contain",
              borderRadius: "8px",
            }}
          />
        </Flex>

        {/* Annotated Image */}
        <Flex direction="column" alignItems="center" gap="10px">
          <ChakraText fontSize="lg" fontWeight="bold">
            Annotated Image
          </ChakraText>
          <img
            src={
              imageUrls.annotatedImage ||
              "https://via.placeholder.com/400x400?text=No+Annotated+Image"
            }
            alt="Annotated"
            style={{
              maxWidth: "400px",
              maxHeight: "600px",
              objectFit: "contain",
              borderRadius: "8px",
            }}
          />
        </Flex>
      </Flex>
    </ModalBody>
  </ModalContent>
</Modal>

    </Flex>
  );
}